import { type RouteLocationNormalized, type NavigationGuardNext } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { useAppStore } from '@/stores/portal-stores/general-stores/app'
import { useLanguageStore } from '@/stores/portal-stores/general-stores/language'
import { useAuthStore } from '@/stores/portal-stores/general-stores/authentication'
import { useUserStore } from '@/stores/portal-stores/general-stores/user'
import { useProgrammeStore } from '@/stores/portal-stores/general-stores/programme'
import { extractLanguage, isLegacyPage } from '@/application/helpers/index'
import { type INativeWrongLang } from '@/interfaces/helpers/ILanguage'
import { useTrackingStore } from '@/stores/portal-stores/general-stores/tracking'
import { useNavigationStore } from '@/stores/portal-stores/component-stores/navigation'
import { usePushNotificationStore } from '@/stores/portal-stores/notification-stores/pushNotificationStore'

export const handleRouteChange = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  console.log('ROUTER FIRST LINE: to.name = ', to, to.fullPath)
  const appStore = useAppStore()
  const authStore = useAuthStore()
  const programmeStore = useProgrammeStore()
  const navigationStore = useNavigationStore()
  const languageStore = useLanguageStore()
  const tracking = useTrackingStore()
  const userStore = useUserStore()
  const pushNotificationStore = usePushNotificationStore()

  if (to.path === '/id-logon-redirect') {
    appStore.setAppSpinner(false)
    next()
  }

  if (appStore.initialized === false) {
    await appStore.setInitialized(true)
  }

  if (appStore.callGetContent === true) {
    appStore.setContent(undefined)
  }

  // Call Authenticate before setting consts
  await authStore.authenticate()
  const isAuthenticated = authStore.isAuthenticated
  const selectedProgramme = programmeStore.selectedProgramme

  let redirectPath: string | undefined = undefined

  console.log('ROUTER: isAuthenticated = ', isAuthenticated)
  console.log('ROUTER: to = ', to)

  // If path os equal to umbraco, redirect to legacy
  if (to.path === '/umbraco' && Capacitor.isNativePlatform() === false) {
    window.location.replace(appStore.apiUrl + to.path)
    return
  }

  if (to.params.path === 'onboarding') {
    // next({ path: '/' })
    return
  } else if (to.path === '/unsubscribe') {
    next()
    return
  }

  if (isAuthenticated === false) {
    if (to.name !== 'Login' && to.name !== 'ChangePassword' && to.name !== 'IdLogon') {
      console.log('ROUTER: GO TO Login')
      redirectPath = to.fullPath === '/' ? undefined : to.fullPath
      next({
        path: '/login',
        query: {
          redirectedFrom: redirectPath
        }
      })
      return
    }
  }

  if (isAuthenticated === true) {
    //Sign in
    if (to.name !== 'ResetPassword' && userStore.member.temporaryPassword === true) {
      console.log('ROUTER: GO TO ResetPassword')
      next({ name: 'ResetPassword' })
      return
    }

    if (
      userStore.member.temporaryPassword === false &&
      to.name !== 'SelectProgramme' &&
      programmeStore.toSelectProgramme() === true &&
      to.name !== 'ContactInformation'
    ) {
      redirectPath = to.fullPath === '/' ? undefined : to.fullPath
      next({
        path: '/select-programme',
        query: {
          redirectedFrom: redirectPath
        }
      })
      return
    }

    //Cookie consent - consent to cookie tracking
    if (
      tracking.consentTracking() === false &&
      to.name !== 'CookieConsent' &&
      programmeStore.toSelectProgramme() === false &&
      userStore.member.temporaryPassword === false
    ) {
      redirectPath = to.fullPath === '/' ? undefined : to.fullPath
      next({
        path: '/cookie-consent',
        query: {
          redirectedFrom: redirectPath
        }
      })
      return
    }

    if (
      userStore.member.temporaryPassword === false &&
      to.name !== 'ContactInformation' &&
      selectedProgramme != undefined &&
      tracking.consentTracking() === true &&
      selectedProgramme.consentDate !== undefined
    ) {
      tracking.initializeTracking()

      if (languageStore.listOfAvailableLanguages == undefined) {
        await languageStore.setListOfAvailableLanguages()
      }

      //Catch pages that you are not allowed to see when Aluthenticated
      if (to.name === 'Login' || to.name === 'changePassword' || to.name === 'CookieConsent') {
        await appStore.getContent('/')
        next({ path: '/' })
        return
      }

      //Catch deeplink to old calendar event url
      if (to.path.includes('/meetings/')) {
        const deepLinkPattern = /\/meetings\/(.+)/
        const match = to.path.match(deepLinkPattern)
        // look for calendarsection in the navigation, so we can use its url
        const navOptions = navigationStore.navigationOptions('calendarsection')

        // check if navOption is to be used in modern design, then change the link to point to modern calendersection
        if (
          match != undefined &&
          navOptions &&
          navOptions.navItem &&
          navOptions.navItem.url &&
          navOptions?.checkLinkExistInNavigation === true &&
          navOptions.useNavigationItemInModern === true
        ) {
          const id = match[1]
          const toPath = to.path.replace(deepLinkPattern, navOptions.navItem.url)
          window.location.href = toPath + '?calenderId=' + id
        } else {
          // else, it's not to be used in modern, therefore we replace the url to link to legacy
          await window.location.replace(appStore.apiUrl + to.fullPath)
          return
        }
      }

      // Initialize push notifications only for native mobile apps (iOS/Android)
      // This will check permissions, register the device with FCM/APNS,
      // and set up notification listeners for both foreground and background notifications
      if (Capacitor.isNativePlatform() === true) {
        await pushNotificationStore.initializePushNotifications()
      }

      navigationStore.setCurrentNavigationUrl('/' + to.path.split('/')[1].toString())
      console.log('ROUTER: GET CONTENT')

      if (to.name !== 'StyleGuide' && appStore.callGetContent === true) {
        await appStore.getContent(to.fullPath)
      }
      // Check for useNewFrontpage on WebApp (Not Native)
      console.log(
        'appStore.siteContent.frontpageSettings?.useNewFrontpage',
        appStore.siteContent.frontpageSettings?.useNewFrontpage
      )

      const navigationOptions = navigationStore.navigationOptions(to.path)

      if (
        (appStore.siteContent.frontpageSettings?.useNewFrontpage === false || to.path !== '/') &&
        navigationOptions.useNavigationItemInModern === false &&
        Capacitor.isNativePlatform() === false
      ) {
        const currentUrl = window.location.hostname

        const extractedLang = extractLanguage(currentUrl)
        const currentLocale = localStorage.getItem('appLanguageLocale')

        if (
          currentLocale &&
          extractedLang?.locale !== currentLocale &&
          navigationOptions.checkLinkExistInNavigation === false
        ) {
          appStore.setNativeLinkIsAnotherLanguage({
            isWrongLang: true,
            locale: extractedLang?.locale,
            lang: extractedLang?.language?.fullName
          } as INativeWrongLang)
        } else if (
          appStore.contentStatus !== 404 &&
          to.name !== 'StyleGuide' &&
          isLegacyPage(appStore.content.type)
        ) {
          console.log('ROUTER: GO TO OLD PORTAL - window.location.replace', to)
          await window.location.replace(appStore.apiUrl + to.fullPath)
          return
        }
      }

      navigationStore.setSignInVisibility(false)
    }

    // Handle 404 page redirection
    if (
      (appStore.contentStatus === 404 && to.name !== 'NotFound') ||
      (appStore.isProd() && to.name === 'StyleGuide')
    ) {
      console.log('ROUTER: GO TO NotFound')
      next({
        name: 'NotFound',
        params: { lastPath: to.fullPath }
      })
      return
    }

    const redirectTo = to.query.redirectTo

    if (redirectTo != undefined) {
      const item = navigationStore.navigationOptions(redirectTo.toString().toLocaleLowerCase())
      let redirectId = undefined
      if (item.navItem?.type === 'jobmatch') {
        redirectId = to.query.jobAdvertId
      }
      next({
        path: item.navItem?.url,
        query: { jobAdvertId: redirectId }
      })
      return
    }
  }

  appStore.setAppSpinner(false)
  next()
}

export default [handleRouteChange]
